'use client';

import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';

import { BookingChipIcon } from './booking-chip-icon';
import styles from './native-booking-controls.module.css';

// Sport metadata from the database.
// `id` is optional here because SportFilterToggle (this component) doesn't
// need it, but the booking flow uses it to resolve sport slug → UUID for the
// get_available_classes_for_date RPC (p_sport_id). It is always populated
// by extractSportsData() in use-booking-reducer.ts.
export interface SportData {
  id?: string;
  slug: string;
  name: string;
  /**
   * Secondary-language (Arabic) sport name set by the tenant (sports.name_alt).
   * Preferred over `name` when the UI renders in Arabic. Optional/NULL when
   * the tenant hasn't configured it.
   */
  name_alt?: string | null;
  icon: string | null;
  color: string | null;
}

interface SportFilterToggleProps {
  value: string | null;
  onChange: (value: string) => void;
  availableSports: string[];
  sportsMetadata?: Record<string, SportData>;
  /**
   * When provided, prepends a full-width "All Classes" pill inside the same
   * bordered container as the sport pills. Used by the studio unified-class
   * filter so the "All" toggle and the sport row share one visual group.
   */
  allClassesPill?: {
    label: string;
    isActive: boolean;
    onSelect: () => void;
  };
  /**
   * Extra category pills (e.g. "Event") rendered on the same row as the sport
   * pills. Used by single-sport studios where the lone sport pill is redundant
   * with "All Classes": instead of stranding the sport's non-group_classes
   * categories in a separate box below, they sit beside "All Classes" as
   * top-level filters. The parent resolves the icon so this component stays
   * presentational.
   */
  categoryPills?: Array<{
    value: string;
    label: string;
    icon?: ReactNode;
    isActive: boolean;
    onSelect: () => void;
  }>;
}

// Fallback icons for known sports when database icon is not available
const fallbackIcons: Record<string, string> = {
  // Sports
  tennis: '/images/book/tennis-racket.svg',
  padel: '/images/book/padel-ball.svg',
  fitness: '/images/book/fitness.svg',
  pilates: '/images/book/fitness.svg',
  'table-tennis': '/images/book/table-tennis.svg',
  therapy: '/images/book/therapy.svg',
  // Clinic / medical services
  physiotherapy: '/images/book/activity.svg',
  dental: '/images/book/sparkles.svg',
  dermatology: '/images/book/hand.svg',
  pediatrics: '/images/book/baby.svg',
  orthopedics: '/images/book/bone.svg',
  ophthalmology: '/images/book/eye.svg',
  'general-medicine': '/images/book/stethoscope.svg',
  ent: '/images/book/ear.svg',
  cardiology: '/images/book/heart-pulse.svg',
  nutrition: '/images/book/apple.svg',
  psychology: '/images/book/brain.svg',
  chiropractic: '/images/book/activity.svg',
  'massage-therapy': '/images/book/hand.svg',
  acupuncture: '/images/book/clipboard-plus.svg',
  consultation: '/images/book/stethoscope.svg',
};

// Get icon source - prioritize database icon, then fallback, then null (no icon)
function getSportIcon(sportSlug: string, metadata?: SportData): string | null {
  // If we have metadata with an icon URL, use it
  if (metadata?.icon) {
    // If icon is a URL (starts with http or /), use it directly
    if (metadata.icon.startsWith('http') || metadata.icon.startsWith('/')) {
      return metadata.icon;
    }
    // Otherwise it might be an emoji or text icon - we'll handle that in render
  }

  // Use fallback icon for known sports
  const fallback = fallbackIcons[sportSlug.toLowerCase()];
  if (fallback) {
    return fallback;
  }

  return null;
}

// Check if icon is an emoji or text (not a URL/path)
function isEmojiIcon(icon: string | null): boolean {
  if (!icon) return false;
  return !icon.startsWith('http') && !icon.startsWith('/');
}

export function SportFilterToggle({
  value,
  onChange,
  availableSports,
  sportsMetadata = {},
  allClassesPill,
  categoryPills = [],
}: SportFilterToggleProps) {
  const { t, i18n } = useTranslation('landing');
  const isArabic = i18n.language?.toLowerCase().startsWith('ar') ?? false;

  if (
    availableSports.length === 0 &&
    !allClassesPill &&
    categoryPills.length === 0
  ) {
    return null;
  }

  // When the "All Classes" pill is present, sport buttons act as a *narrowing*
  // filter — clicking one switches off "All" and scopes to that sport. When
  // "All" is active, dim the sport pills to communicate they're inactive.
  const sportsDimmed = allClassesPill?.isActive === true;

  return (
    <div
      className={cn(
        'bg-muted/30 w-full gap-1 rounded-lg border p-1',
        styles.typeToggle,
        allClassesPill ? 'flex flex-col' : 'flex',
      )}
    >
      {allClassesPill && (
        <button
          aria-pressed={allClassesPill.isActive}
          type="button"
          onClick={() => {
            if (!allClassesPill.isActive) allClassesPill.onSelect();
          }}
          className={cn(
            styles.typeOption,
            'flex w-full items-center justify-center rounded-md border px-3 py-2 text-xs font-medium transition-all md:text-sm',
            allClassesPill.isActive
              ? 'border-primary/35 bg-primary/5 text-foreground shadow-none'
              : 'border-border bg-background text-foreground hover:border-primary/40 shadow-sm hover:shadow-md',
          )}
        >
          {allClassesPill.label}
        </button>
      )}
      <div
        className={cn(
          'flex w-full gap-1',
          allClassesPill &&
            availableSports.length === 0 &&
            categoryPills.length === 0 &&
            'hidden',
        )}
      >
        {availableSports.map((sportSlug) => {
          const metadata = sportsMetadata[sportSlug];
          const fallbackName =
            metadata?.name ||
            sportSlug.charAt(0).toUpperCase() + sportSlug.slice(1);
          // Arabic UI: the tenant's configured name_alt wins over everything, so a
          // tenant naming a sport "صالة رياضية" shows that — not the English DB
          // name and not a generic hardcoded sports.<slug> string. When name_alt
          // is unset we fall through to the shared i18n key (e.g. "كرة قدم"), then
          // the English name. Non-Arabic UI keeps the prior i18n-key-first behavior.
          const arabicName = isArabic ? metadata?.name_alt?.trim() : undefined;
          const displayName =
            arabicName ||
            t(`sports.${sportSlug}`, { defaultValue: fallbackName });
          const useEmojiIcon = metadata?.icon && isEmojiIcon(metadata.icon);
          const iconSrc = getSportIcon(sportSlug, metadata);

          return (
            <button
              key={sportSlug}
              aria-pressed={value === sportSlug && !sportsDimmed}
              type="button"
              onClick={() => {
                // When the All Classes pill is active, every sport tap is a real
                // selection — even tapping the sport that happens to match the
                // carried-over state.sport must dispatch so the parent can exit
                // "All" mode. Without this, the leftmost sport (which usually
                // matches the default state.sport) becomes a no-op.
                if (sportsDimmed || value !== sportSlug) onChange(sportSlug);
              }}
              className={cn(
                styles.typeOption,
                'flex flex-1 flex-row items-center justify-center gap-1 rounded-md border px-1 py-2 text-xs font-medium transition-all md:gap-2 md:px-3 md:text-sm',
                value === sportSlug && !sportsDimmed
                  ? 'border-primary/35 bg-primary/5 text-foreground shadow-none'
                  : 'border-border bg-background text-foreground hover:border-primary/40 shadow-sm hover:shadow-md',
              )}
            >
              {useEmojiIcon ? (
                <span className="flex-shrink-0 text-base">
                  {metadata?.icon}
                </span>
              ) : iconSrc ? (
                <BookingChipIcon src={iconSrc} alt={displayName} />
              ) : null}
              <span className="text-center leading-tight">{displayName}</span>
            </button>
          );
        })}
        {categoryPills.map((pill) => (
          <button
            key={pill.value}
            aria-pressed={pill.isActive}
            type="button"
            onClick={() => {
              if (!pill.isActive) pill.onSelect();
            }}
            className={cn(
              styles.typeOption,
              'flex flex-1 flex-row items-center justify-center gap-1 rounded-md border px-1 py-2 text-xs font-medium transition-all md:gap-2 md:px-3 md:text-sm',
              pill.isActive
                ? 'border-primary/35 bg-primary/5 text-foreground shadow-none'
                : 'border-border bg-background text-foreground hover:border-primary/40 shadow-sm hover:shadow-md',
            )}
          >
            {pill.icon}
            <span className="text-center leading-tight">{pill.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
